
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const CookieConsent = () => {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('sethwood_cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('sethwood_cookie_consent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('sethwood_cookie_consent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('sethwood_cookie_consent', JSON.stringify(consentData));
    setIsVisible(false);
    setShowPreferences(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 sm:p-6 pointer-events-none">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm pointer-events-auto animate-[fadeIn_0.3s_ease-out]"
        onClick={() => {}}
      />
      
      {/* Cookie Banner */}
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl pointer-events-auto animate-[slideUp_0.4s_ease-out] mb-4">
        {!showPreferences ? (
          /* Main Banner */
          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#7A8B7F]/10 rounded-full flex-shrink-0 overflow-hidden">
                <img 
                  src="https://cloud.hayeen.com/raw/Picture/sethwood/sethwood-favicon.ico.png" 
                  alt="SETHWOOD Logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-serif text-[#1A1A1A] mb-2">
                  {t('cookie.title')}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  {t('cookie.description')}
                </p>
                <p className="text-xs text-gray-500 mb-6">
                  {t('cookie.learnMore')}{' '}
                  <button 
                    className="text-[#7A8B7F] underline hover:text-[#5A6B5F] cursor-pointer"
                    onClick={() => setShowPreferences(true)}
                  >
                    {t('cookie.privacyPolicy')}
                  </button>
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-3 bg-[#1A1A1A] text-white text-sm font-medium rounded-full hover:bg-[#333] transition-colors duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {t('cookie.acceptAll')}
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-6 py-3 bg-gray-100 text-[#1A1A1A] text-sm font-medium rounded-full hover:bg-gray-200 transition-colors duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {t('cookie.rejectAll')}
                  </button>
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="px-6 py-3 border border-gray-300 text-[#1A1A1A] text-sm font-medium rounded-full hover:border-gray-400 hover:bg-gray-50 transition-colors duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {t('cookie.customize')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Preferences Panel */
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-serif text-[#1A1A1A]">
                {t('cookie.preferencesTitle')}
              </h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-[#1A1A1A]">{t('cookie.necessary')}</span>
                  <span className="text-xs text-[#7A8B7F] bg-[#7A8B7F]/10 px-2 py-1 rounded-full">
                    {t('cookie.alwaysActive')}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{t('cookie.necessaryDesc')}</p>
              </div>
              
              {/* Analytics Cookies */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-[#1A1A1A]">{t('cookie.analytics')}</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7A8B7F]"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-600">{t('cookie.analyticsDesc')}</p>
              </div>
              
              {/* Marketing Cookies */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-[#1A1A1A]">{t('cookie.marketing')}</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7A8B7F]"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-600">{t('cookie.marketingDesc')}</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSavePreferences}
                className="px-6 py-3 bg-[#1A1A1A] text-white text-sm font-medium rounded-full hover:bg-[#333] transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                {t('cookie.savePreferences')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-3 bg-gray-100 text-[#1A1A1A] text-sm font-medium rounded-full hover:bg-gray-200 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                {t('cookie.acceptAll')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
